import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { showErrorToast, showSuccessToast } from "../../components/common/ShowToast";
import { getSubmissions, reviewCompetencyAnswer } from "./services/jobsStorage";
import type { ApplicationSubmission, CompetencyAnswer } from "./types";

const CompetencyReviewPage: React.FC = () => {
  const theme = useTheme();
  const [submissions, setSubmissions] = useState<ApplicationSubmission[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, CompetencyAnswer>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    void getSubmissions()
      .then((data) => {
        setSubmissions(data);
        setSelectedId(data[0]?.id ?? null);
      })
      .catch(() => showErrorToast("Unable to load competency reviews.", "competency-review-load-error"))
      .finally(() => setLoading(false));
  }, []);

  const selectedSubmission = submissions.find((submission) => submission.id === selectedId) ?? null;

  useEffect(() => {
    if (!selectedSubmission) {
      setAnswers({});
      return;
    }
    setAnswers(
      Object.fromEntries(
        selectedSubmission.competencyAnswers
          .filter((answer) => answer.answerId)
          .map((answer) => [answer.answerId as string, answer]),
      ),
    );
  }, [selectedSubmission]);

  const updateAnswer = (answer: CompetencyAnswer) => {
    if (!answer.answerId) return;
    setAnswers((current) => ({ ...current, [answer.answerId as string]: answer }));
  };

  const saveReview = async (answer: CompetencyAnswer) => {
    if (!selectedSubmission || !answer.answerId || answer.reviewPercentage === null || answer.reviewPercentage === undefined) {
      return;
    }
    try {
      setSavingId(answer.answerId);
      const saved = await reviewCompetencyAnswer(
        selectedSubmission.id,
        answer.answerId,
        answer.reviewPercentage,
        answer.reviewFeedback ?? "",
      );
      updateAnswer({
        ...answer,
        scoreEarned: saved.score_awarded,
        reviewPercentage: saved.review_percentage,
        reviewFeedback: saved.review_feedback,
        reviewPending: false,
      });
      showSuccessToast("Answer review saved.");
    } catch {
      showErrorToast("Unable to save this answer review.", "competency-review-save-error");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <LinearProgress sx={{ color: theme.palette.primary.main }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
        Competency Reviews
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2 }}>
        <Paper elevation={0} sx={{ minHeight: 500 }}>
          <List disablePadding>
            {submissions.map((submission) => (
              <ListItemButton
                key={submission.id}
                selected={submission.id === selectedId}
                onClick={() => setSelectedId(submission.id)}
              >
                <ListItemText
                  primary={submission.candidateInfo.fullName}
                  secondary={`${submission.jobTitle} | ${submission.competencyAnswers.length} answers`}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper elevation={0} sx={{ p: 2.5, minHeight: 500 }}>
          {!selectedSubmission ? (
            <Typography color="text.secondary">Select an application to review.</Typography>
          ) : (
            <>
              <Typography variant="h6" fontWeight={800}>{selectedSubmission.candidateInfo.fullName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedSubmission.jobTitle}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {selectedSubmission.competencyAnswers.map((originalAnswer, index) => {
                const answer = (originalAnswer.answerId && answers[originalAnswer.answerId]) || originalAnswer;
                if (!answer.openTextAnswer) return null;
                return (
                  <Box key={answer.answerId ?? index} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {answer.questionText || `Question ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
                      {answer.openTextAnswer}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {answer.reviewPending ? "Review Pending" : `Score: ${answer.scoreEarned} pts`} | {answer.reviewPercentage ?? 0}%
                    </Typography>
                    <Slider
                      value={answer.reviewPercentage ?? 0}
                      min={0}
                      max={100}
                      valueLabelDisplay="auto"
                      onChange={(_, value) => updateAnswer({
                        ...answer,
                        reviewPercentage: Array.isArray(value) ? value[0] : value,
                        reviewPending: false,
                      })}
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      label="Feedback"
                      value={answer.reviewFeedback ?? ""}
                      onChange={(event) => updateAnswer({ ...answer, reviewFeedback: event.target.value })}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={savingId === answer.answerId}
                      onClick={() => void saveReview(answer)}
                      sx={{ mt: 1, textTransform: "none" }}
                    >
                      {savingId === answer.answerId ? "Saving..." : "Save Review"}
                    </Button>
                  </Box>
                );
              })}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default CompetencyReviewPage;
